import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvitationDto } from 'woofwoof-api';
import { invitationApi } from 'src/api/invitation';
import { organizationApi } from 'src/api/organization';
import { userApi } from 'src/api/user';
import { useUserContext } from 'src/auth/useUserContext';
import { preloadStaffLandingPages } from 'src/routes/elements';
import { PATH_DASHBOARD } from 'src/routes/paths';

export interface InvitationFlow {
  invites: InvitationDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onAccept: (invitation: InvitationDto) => Promise<void>;
  onReject: (invitation: InvitationDto) => Promise<void>;
  onDashboard: () => Promise<void>;
}

/** Invited newcomers' last onboarding step: decide on invitations, then finish onboarding. */
export function useInvitationFlow(): InvitationFlow {
  const user = useUserContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const onboarded = useRef(false);
  const queryKey = ['invites', user.data?.id];
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: async () =>
      (await invitationApi.invitationControllerGetInvitationsByUserId()).data,
    enabled: Boolean(user.data?.id),
    retry: 1,
  });

  const onDashboard = async (): Promise<void> => {
    if (!user.data?.onboarded && !onboarded.current) {
      await userApi.userControllerOnboardUser();
      onboarded.current = true;
    }
    await getAuth().currentUser?.getIdToken(true);
    // Fetch before updating either context: UserContext.refetch exposes isFetching
    // as loading and would unmount this step, losing a failed refresh's retry state.
    const [refreshedUser, refreshedOrganizations] = await Promise.all([
      userApi.userControllerGetCurrentUser(),
      organizationApi.organizationControllerGetOrganizationsByUserId({
        userId: user.data!.id,
      }),
      // A failed prefetch only costs a loader on the next page.
      preloadStaffLandingPages().catch(() => undefined),
    ]);
    // An older user request still in flight must not overwrite the onboarded user.
    await queryClient.cancelQueries(['user']);
    queryClient.setQueryData(
      ['user-organizations', user.data!.id],
      refreshedOrganizations,
    );
    queryClient.setQueryData(['user'], refreshedUser);
    // react-query delivers cache updates on a timer; route only after both
    // contexts carry the new membership, or the dashboard guard sees the old one.
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
    navigate(PATH_DASHBOARD.root, { replace: true });
  };

  const decide = async (
    invitation: InvitationDto,
    accept: boolean,
  ): Promise<void> => {
    const request = { processInvitationDto: { token: invitation.token } };
    await (accept
      ? invitationApi.invitationControllerAcceptInvitation(request)
      : invitationApi.invitationControllerDeclineInvitation(request));
    queryClient.setQueryData<InvitationDto[]>(queryKey, (previous) =>
      previous?.filter((item) => item.id !== invitation.id),
    );
  };

  return {
    invites: data || [],
    isLoading,
    isError,
    onRetry: () => {
      void refetch();
    },
    onAccept: (invitation) => decide(invitation, true),
    onReject: (invitation) => decide(invitation, false),
    onDashboard,
  };
}
